import React, { useEffect, useState } from 'react'
import { ApexLineChart, BoxCard } from './component/Charts'
import { DateRangePicker } from '../Table/component/Table'

import { FaRegLightbulb } from 'react-icons/fa'
import { IoCartOutline } from 'react-icons/io5'
import { IoMdCar } from 'react-icons/io'
import TestPieChart from './component/Charts/TestPieChart'

const SiteEmission = ({ siteName, siteData, year }) => {
  const [dateRange, setDateRange] = useState({ start: `Jan ${year}`, end: `Dec ${year}` })

  // Update dateRange whenever siteName or year changes
  useEffect(() => {
    setDateRange({ start: `Jan ${year}`, end: `Dec ${year}` })
  }, [siteName, year])

  const monthlyDataSeries = [
    { name: 'Scope 1', data: siteData?.monthlyByScope?.scope1 || Array(12).fill(0) },
    { name: 'Scope 2', data: siteData?.monthlyByScope?.scope2 || Array(12).fill(0) },
    { name: 'Scope 3', data: siteData?.monthlyByScope?.scope3 || Array(12).fill(0) },
  ]
  const monthCategories = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  // Helper function to extract top slices for pie chart
  const getPieChartData = (scopeNumber, limit) => {
    let slices = (siteData?.categoryBreakdown || [])
      .filter(cat => cat.scope === scopeNumber)
      .sort((a, b) => b.total - a.total);

    // Default empty array if no slices
    if (slices.length === 0) {
      return { data: [1], labels: ['No Data'] };
    }

    // Only capture up to the limit
    const topSlices = slices.slice(0, limit);

    // Sum the remaining ones into 'Others'
    const others = slices.slice(limit);
    let othersSum = others.reduce((acc, curr) => acc + curr.total, 0);

    if (othersSum > 0) {
      topSlices.push({ name: 'Others', total: othersSum });
    }

    // If we only have zero numbers overall, show 0 instead of nothing
    if (topSlices.every(s => s.total === 0)) {
      return { data: topSlices.map(() => 0), labels: topSlices.map(s => s.name) };
    }

    return {
      data: topSlices.map(s => Number(s.total.toFixed(2))),
      labels: topSlices.map(s => s.name)
    }
  }

  const pie1 = getPieChartData(1, 4);
  const pie2 = getPieChartData(2, 2);
  const pie3 = getPieChartData(3, 3);

  return (
    <div className="w-full h-full overflow-y-auto max-h-full max-w-full border p-4 rounded-xl mb-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{siteName} - Emission</h1>
        <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* SCOPE BOXES */}
        <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <BoxCard
            title="SCOPE 1"
            value={`${Number(siteData?.totalScope1 || 0).toFixed(2)}`}
            unit="tCO2e"
            Icon={IoMdCar}
            extraColor="bg-scope1"
          />
          <BoxCard
            title="SCOPE 2"
            value={`${Number(siteData?.totalScope2 || 0).toFixed(2)}`}
            unit="tCO2e"
            Icon={FaRegLightbulb}
            extraColor="bg-scope2"
          />
          <BoxCard
            title="SCOPE 3"
            value={`${Number(siteData?.totalScope3 || 0).toFixed(2)}`}
            unit="tCO2e"
            Icon={IoCartOutline}
            extraColor="bg-scope3"
          />
        </div>

        {/* PIE CHARTS */}
        <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <TestPieChart
            data={pie1.data}
            labels={pie1.labels}
            title="Scope 1 breakdown"
            colors={['#FF8C00', '#FFA733', '#FFBF66', '#FFD199', '#FFE5CC']}
          />
          <TestPieChart
            data={pie2.data}
            labels={pie2.labels}
            title="Scope 2 breakdown"
            colors={['#0066FF', '#3399FF', '#66CCFF', '#99E6FF']}
          />
          <TestPieChart
            data={pie3.data}
            labels={pie3.labels}
            title="Scope 3 breakdown"
            colors={['#00B050', '#33CC66', '#66FF99', '#99FFCC']}
          />
        </div>

        {/* LINE CHART */}
        <div className="col-span-12 border border-gray-300 rounded-md shadow-lg p-6 mt-6">
          <ApexLineChart
            title="Monthly Emission by Scope"
            data={monthlyDataSeries}
            categories={monthCategories}
          />
        </div>
      </div>
    </div>
  )
}

export default SiteEmission
